import { BOQ, Contract, Organization, Product, Project, Supplier } from "../types";
import { getFixedAuthToken, getAuthorizationHeader } from "./authConfig";

// Базовый URL через Vite proxy (/1c-api -> https://clode.ru/38907428)
const API_BASE = "/1c-api/odata/standard.odata/";

/** Заголовок Authorization для запросов к 1С */
const get1CAuthHeader = (): string => getAuthorizationHeader(getFixedAuthToken());

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const fetchWithRetry = async (url: string, options: RequestInit, retries = 3): Promise<Response> => {
  try {
    // Запрос к 1С через Vite dev proxy
    const response = await fetch(url, options);

    if (response.status === 401) return response;
    if (!response.ok && response.status >= 500) {
      throw new Error(`Server Error: ${response.status}`);
    }

    return response;
  } catch (err: any) {
    if (retries > 0 && err.name !== 'AbortError') {
      console.warn(`Fetch failed. Retrying... (${retries} left)`);
      await delay(1000);
      return fetchWithRetry(url, options, retries - 1);
    }
    throw new Error(`Network error: ${err.message}. Check your internet or CORS extension.`);
  }
};

const call1C = async (resource: string, params: string = ""): Promise<any[]> => {
  try {
    const targetUrl = `${API_BASE}${resource}?$format=json${params}`;

    const response = await fetchWithRetry(targetUrl, {
      method: 'GET',
      headers: { 
        'Authorization': get1CAuthHeader(), 
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      if (response.status === 401) throw new Error("Unauthorized");
      return [];
    }

    const data = await response.json();
    return data.value || [];
  } catch (error: any) {
    console.error(`[1C API Error] ${resource}:`, error.message);
    return [];
  }
};

/** Проверка подключения к 1С (использует учётные данные из .env) */
export const check1CConnection = async (): Promise<boolean> => {
  const auth = getFixedAuthToken();
  if (!auth) return false;
  const targetUrl = `${API_BASE}?$format=json`;
  const response = await fetchWithRetry(targetUrl, {
    method: 'GET',
    headers: { 'Authorization': getAuthorizationHeader(auth) }
  });
  return response.status === 200;
};

export const searchOrganizations = (query: string) => 
  call1C("Catalog_Организации", `&$filter=substringof('${encodeURIComponent(query)}', Description) eq true&$select=Ref_Key,Description&$top=10`);

export const searchProjects = (query: string) => 
  call1C("Catalog_Проекты", `&$filter=substringof('${encodeURIComponent(query)}', Description) eq true&$select=Ref_Key,Description&$top=10`);

export const searchSuppliers = (query: string) => 
  call1C("Catalog_Контрагенты", `&$filter=substringof('${encodeURIComponent(query)}', Description) eq true&$select=Ref_Key,Description&$top=10`);

export const getContracts = (orgId: string, suppId: string) => 
  call1C("Catalog_ДоговорыКонтрагентов", `&$filter=Owner eq guid'${suppId}' and Организация_Key eq guid'${orgId}' and DeletionMark eq false`);

export const searchNomenclature = async (query: string): Promise<Product[]> => {
  const safeQuery = encodeURIComponent(query.replace(/'/g, "''"));
  const params = `&$filter=substringof('${safeQuery}', ДополнительныеРеквизиты/Значение) eq true and ДополнительныеРеквизиты/LineNumber eq 1&$select=Ref_Key,ДополнительныеРеквизиты/Значение,ЕдиницаИзмерения/МеждународноеСокращение&$expand=ЕдиницаИзмерения&$top=15`;
  const raw = await call1C("Catalog_Номенклатура", params);
  return raw.map(item => ({
    Ref_Key: item.Ref_Key,
    Description: item.ДополнительныеРеквизиты?.[0]?.Значение || "Unknown",
    Unit: item.ЕдиницаИзмерения?.МеждународноеСокращение || "pcs"
  }));
};

export const getBOQs = () => call1C("Document_ЗаказПокупателя", `&$select=Ref_Key,Number,Комментарий&$top=50`);

export const submitOrder = async (data: any) => {
  console.log("Submitting order payload:", data);
  await delay(800);
};
